import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Images, Link2, Unlink, RotateCcw, Layers, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, uid } from "@/lib/api";
import { submitCapture } from "@/lib/offline";
import { prepareImage } from "@/lib/image";
import { PageHeader, StatusChip, fmtDate } from "@/components/common";
import ActiveEventPicker from "@/components/ActiveEventPicker";

// items: [{key, files:[File], status, error, client_id}]
export default function Batch() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [picked, setPicked] = useState([]);
  const [batches, setBatches] = useState([]);
  const [limits, setLimits] = useState({ max_batch_files: 100, max_file_mb: 15 });
  const [running, setRunning] = useState(false);
  const batchId = useRef(null);

  useEffect(() => {
    api.get("/batches").then((r) => setBatches(r.data));
    api.get("/integrations").then((r) => setLimits(r.data.limits)).catch(() => {});
  }, []);

  const addFiles = (list) => {
    const files = [...list];
    const tooBig = files.filter((f) => f.size > limits.max_file_mb * 1024 * 1024);
    if (tooBig.length) toast.error(`${tooBig.length} file(s) exceed ${limits.max_file_mb} MB and were skipped`);
    const ok = files.filter((f) => f.size <= limits.max_file_mb * 1024 * 1024);
    if (items.length + ok.length > limits.max_batch_files) return toast.error(`Maximum ${limits.max_batch_files} cards per batch`);
    setItems((p) => [...p, ...ok.map((f) => ({ key: uid(), files: [f], status: "ready", client_id: uid(), url: URL.createObjectURL(f) }))]);
  };
  const togglePick = (key) => setPicked((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key].slice(-2)));
  const pair = () => {
    const [a, b] = picked.map((k) => items.find((i) => i.key === k));
    setItems((p) => p.filter((i) => i.key !== b.key).map((i) => (i.key === a.key ? { ...i, files: [a.files[0], b.files[0]], backUrl: b.url } : i)));
    setPicked([]);
  };
  const unpair = (it) => setItems((p) => p.flatMap((i) => (i.key === it.key ? [{ ...i, files: [i.files[0]], backUrl: null }, { key: uid(), files: [i.files[1]], status: "ready", client_id: uid(), url: i.backUrl }] : [i])));
  const update = (key, patch) => setItems((p) => p.map((i) => (i.key === key ? { ...i, ...patch } : i)));

  const uploadOne = async (it) => {
    update(it.key, { status: "uploading", error: null });
    try {
      const files = await Promise.all(it.files.map(prepareImage));
      const r = await submitCapture({ kind: "upload", files, names: files.map((f) => f.name), source_type: "batch_image", batch_id: batchId.current, client_id: it.client_id });
      update(it.key, { status: r.synced ? "saved" : "local" });
    } catch (e) { update(it.key, { status: "failed", error: errMsg(e) }); }
  };

  const start = async () => {
    setRunning(true);
    try {
      if (!batchId.current) batchId.current = (await api.post("/batches", { expected_files: items.length })).data.id;
    } catch (e) { toast.error(errMsg(e)); setRunning(false); return; }
    const queue = items.filter((i) => ["ready", "failed"].includes(i.status));
    const workers = Array.from({ length: 3 }, async () => { while (queue.length) await uploadOne(queue.shift()); });
    await Promise.all(workers);
    setRunning(false);
    toast.success("Upload finished — extraction continues in the background");
  };

  const done = items.length > 0 && items.every((i) => ["saved", "local"].includes(i.status));
  return (
    <div>
      <PageHeader title="Batch upload" subtitle={`Upload up to ${limits.max_batch_files} card photos. One card per image by default; tap two images to pair front and back.`} />
      <div className="mb-4"><ActiveEventPicker /></div>
      <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-300 bg-white p-8 text-center hover:border-teal-500" data-testid="batch-dropzone">
        <Images className="h-8 w-8 text-teal-600" />
        <span className="font-semibold">Choose card photos</span>
        <span className="text-xs text-slate-500">JPEG, PNG, WEBP or HEIC · max {limits.max_file_mb} MB each</span>
        <input data-testid="batch-file-input" type="file" multiple accept="image/*" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
      </label>
      {items.length > 0 && (
        <>
          <div className="my-4 flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold">{items.length} card(s)</span>
            <button data-testid="batch-pair-btn" disabled={picked.length !== 2 || running} onClick={pair} className="cd-btn-secondary h-9 px-3"><Link2 className="h-4 w-4" />Pair as front + back</button>
            <button data-testid="batch-start-btn" disabled={running || done} onClick={start} className="cd-btn-primary ml-auto">{running ? "Uploading…" : items.some((i) => i.status === "failed") ? "Retry failed" : "Upload & extract all"}</button>
            {batchId.current && <button data-testid="batch-review-btn" onClick={() => nav(`/batch/${batchId.current}`)} className="cd-btn-secondary">Review batch<ChevronRight className="h-4 w-4" /></button>}
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
            {items.map((it, i) => (
              <div key={it.key} data-testid={`batch-item-${i}`} onClick={() => it.status === "ready" && togglePick(it.key)}
                className={`relative cursor-pointer overflow-hidden rounded-xl border-2 bg-white ${picked.includes(it.key) ? "border-teal-600" : "border-slate-200"}`}>
                <div className="flex h-28">
                  <img src={it.url} alt="" className="h-full w-full object-cover" />
                  {it.backUrl && <img src={it.backUrl} alt="" className="h-full w-1/2 object-cover" />}
                </div>
                <div className="flex items-center justify-between gap-1 p-2">
                  {it.status === "ready" ? <span className="text-xs text-slate-500">{it.files.length === 2 ? "Front + back" : "Single"}</span> : <StatusChip status={it.status} />}
                  {it.files.length === 2 && it.status === "ready" && <button onClick={(e) => { e.stopPropagation(); unpair(it); }} className="text-slate-400"><Unlink className="h-4 w-4" /></button>}
                  {it.status === "failed" && <button onClick={(e) => { e.stopPropagation(); uploadOne(it); }} data-testid={`batch-item-retry-${i}`}><RotateCcw className="h-4 w-4 text-rose-600" /></button>}
                </div>
                {it.error && <p className="px-2 pb-2 text-[11px] text-rose-600">{it.error}</p>}
              </div>
            ))}
          </div>
        </>
      )}
      <h2 className="mb-3 mt-10 text-lg font-bold">Previous batches</h2>
      <div className="space-y-2">
        {batches.length === 0 && <p className="text-sm text-slate-500">No batches yet.</p>}
        {batches.map((b) => (
          <Link key={b.id} to={`/batch/${b.id}`} data-testid={`batch-link-${b.id}`} className="cd-card flex items-center justify-between py-3 hover:border-teal-500">
            <div><p className="flex items-center gap-2 font-semibold"><Layers className="h-4 w-4 text-teal-600" />{b.name}</p><p className="text-xs text-slate-500">{fmtDate(b.created_at)}</p></div>
            <div className="flex flex-wrap justify-end gap-1">{Object.entries(b.counts).map(([s, n]) => <span key={s} className="text-xs text-slate-600">{n} <StatusChip status={s} /></span>)}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}
